import React from "react";
import cn from "classnames";
import SupportingText from "../SupportingText";

// TODO: Truthtable
const hasValue = val =>
  val !== null && !(Array.isArray(val) && val.length === 0);
const isFilled = val => hasValue(val) && val !== "";

const LabelText = ({ color, placeholder }) => (
  <SupportingText textColor={color} fontWeight="thin" inline>
    {placeholder}
  </SupportingText>
);

const Label = ({ style, color, placeholder }) => (
  <div style={style} className="pr-2 py-1">
    <LabelText color={color} placeholder={placeholder} />
  </div>
);

const DEFAULT_UNDERLINE_COLOR = "#EF5753"; // red-light

/**
 * Function which returns the styles for the Underline of the
 * Text Input.
 *
 * @param {boolean} focused
 * @param {string} underlineColor
 */

const getUnderlineStyle = (focused, underlineColor) => {
  const baseStyle = {
    position: "absolute",
    right: 0,
    left: 0,
    bottom: -0.5,
    borderWidth: 0.5,
    borderStyle: "solid",
    transform: "scaleX(0)",
    transition: "border-color 0.2s ease-out, transform 0.3s ease-out",
    willChange: "transform, border-color",
    pointerEvents: "none"
  };

  // If the textinput is focused, we scale the underline
  // to the full width and animate it.
  const focusedStyle = {
    borderColor: underlineColor,
    transform: "scaleX(1)"
  };

  return focused ? Object.assign(baseStyle, focusedStyle) : baseStyle;
};

/**
 * Function which returns the styles of the label of the text input.
 * @param {boolean} focused
 * @param {boolean} filled
 * @param {boolean} floatingLabel
 */
const getLabelStyle = (focused, filled, floatingLabel) => {
  const baseStyle = {
    position: "absolute",
    bottom: 0,
    left: 2,
    opacity: floatingLabel ? 0 : 1,
    pointerEvents: "none",
    transition: "opacity 0.15s ease-out",
    willChange: "opacity"
  };

  const focusedStyle = {
    opacity: 1
  };

  const filledStyle = {
    opacity: 0
  };

  if (filled) return Object.assign(baseStyle, filledStyle);
  if (focused) return Object.assign(baseStyle, focusedStyle);

  return baseStyle;
};

/**
 * Function which returns the styles for the floating label.
 *
 * @param {boolean} focused
 * @param {boolean} filled
 */
const getFloatingLabelStyle = (focused, filled) => {
  const baseStyle = {
    position: "absolute",
    bottom: 0,
    left: 2,
    pointerEvents: "none",
    transformOrigin: "0 50%",
    transform: "scale(1) translateY(0)",
    transition: "transform 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)",
    willChange: "transform"
  };

  const focusedStyle = {
    transform: "scale(0.85) translateY(-30px)",
    left: 0
  };

  if (focused || filled) return Object.assign(baseStyle, focusedStyle);
  return baseStyle;
};

export default class Input extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      value: null
    };
  }

  handleFocus = ev => {
    this.setState({
      focused: true
    });

    if (this.props.onFocus) {
      this.props.onFocus(ev);
    }
  };

  handleBlur = ev => {
    this.setState({
      focused: false
    });

    if (this.props.onBlur) {
      this.props.onBlur(ev);
    }
  };

  handleChange = ev => {
    if (this.props.onChange) {
      this.props.onChange(ev);
    }

    this.setState({
      value: ev.target.value
    });
  };

  render() {
    const { focused, value } = this.state;
    const {
      type = "text",
      fullWidth = false,
      text = "grey-darkest",
      underlineColor = DEFAULT_UNDERLINE_COLOR,
      floatingLabel = false,
      placeholder = "",
      disabled,
      name
    } = this.props;

    const isValueFilled = isFilled(value);

    return (
      <div
        style={{ height: "50px" }}
        className={cn(
          fullWidth && `w-full`,
          `border-b`,
          `border-grey`,
          `relative`,
          `inline-flex`,
          `items-end`
        )}
      >
        {floatingLabel ? (
          <div>
            <Label
              style={getFloatingLabelStyle(focused, isValueFilled)}
              color={focused ? "red-light" : "grey"}
              placeholder={placeholder}
            />
            <Label
              style={getLabelStyle(focused, isValueFilled, floatingLabel)}
              color="grey"
              placeholder={placeholder}
            />
          </div>
        ) : (
          <Label
            style={getLabelStyle(focused, isValueFilled, floatingLabel)}
            color="grey"
            placeholder={placeholder}
          />
        )}
        <input
          style={{ width: "inherit" }}
          type={type}
          name={name}
          className={cn(
            `leading-normal`,
            `text-base`,
            `font-thin`,
            `pr-2`,
            `py-1`,
            `bg-transparent`,
            `outline-0`,
            "text-sm",
            text && `text-${text}`
          )}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          disabled={disabled}
        />
        <div style={getUnderlineStyle(focused, underlineColor)} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/TextInput/TextInput.js